import { fetchEventSource } from '@microsoft/fetch-event-source'
import { useCallback, useEffect, useRef, useState } from 'react'
import gql from 'graphql-tag'
import { v4 as uuid } from 'uuid'
import { LlmChatMessageSenderType } from '@/__gen/types'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import { PollLlmChatQuery, PollLlmChatPubQuery, PollLlmChatQueryVariables, PollLlmChatPubQueryVariables, usePollLlmChatLazyQuery, usePollLlmChatPubLazyQuery, useSendMessageMutation, useSendMessagePubMutation } from './__gen/useLlm'

gql`
	mutation sendMessage($id: ID!, $content: String!, $form: ID!, $chat: ID!, $recordId: ID!) {
		llmChatMessage_Update(
			input: { id: $id, content: $content, chat: $chat, record: $recordId, form: $form }
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

gql`
	mutation sendMessagePub($id: ID!, $content: String!, $form: ID!, $chat: ID!, $recordId: ID!) {
		llmChatMessagePub_Update(
			input: { id: $id, content: $content, chat: $chat, record: $recordId, form: $form }
		) {
			object {
				id
				chat {
					id
				}
			}
			errors {
				field
				messages
			}
		}
	}
`

const GET_MESSAGES_QUERY = gql`
	query PollLlmChat($chatId: ID!) {
		llmChat(id: $chatId) {
			id
			created
			processingCutoffSeconds
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`

const GET_MESSAGES_PUB_QUERY = gql`
	query PollLlmChatPub($chatId: ID!) {
		llmChatPub(id: $chatId) {
			id
			created
			processingCutoffSeconds
			messages(orderBy: [{ field: created, direction: ASC }]) {
				id
				content
				created
				sender
				responseReceivedAt
			}
		}
	}
`

const ENDPOINTS = {
	'auth': {
		sendMessage: useSendMessageMutation,
		pollQuery: usePollLlmChatLazyQuery,
		getMessagesQuery: GET_MESSAGES_QUERY,
	},
	'pub': {
		sendMessage: useSendMessagePubMutation,
		pollQuery: usePollLlmChatPubLazyQuery,
		getMessagesQuery: GET_MESSAGES_PUB_QUERY,
	},
}

const UNSET_PROCESSING_CUTOFF = -1

export const useLlm = ({ chatId, resetChatId }: { chatId: string; resetChatId: () => void }) => {
	const formContext = useFormContext()
	const endpoint = formContext.publicForm ? 'pub' : 'auth'
	const codeHeader = {context: {
		headers: formContext.publicForm ? {
			'X-Public-Form-Code': formContext.publicForm.code,
		} : {},
	},}

	const [sendMessage, { loading: sending }] = ENDPOINTS[endpoint].sendMessage(codeHeader)
	const [get, { data }] = ENDPOINTS[endpoint].pollQuery(codeHeader)
	const [startingStream, setStartingStream] = useState(false)
	const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const variables = { chatId }

	const lastMessageTimestamp = (
		!data ? null :
		'llmChat' in data ? data?.llmChat?.messages[data?.llmChat?.messages.length - 1]?.created :
		data?.llmChatPub?.messages[data?.llmChatPub?.messages.length - 1]?.created
	)
	const processingCutoffSeconds = (
		!data ? UNSET_PROCESSING_CUTOFF :
		'llmChat' in data ? data?.llmChat?.processingCutoffSeconds :
		data?.llmChatPub?.processingCutoffSeconds
	) || UNSET_PROCESSING_CUTOFF

	const resetInactivityTimer = useCallback(() => {
		if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
		if (processingCutoffSeconds === UNSET_PROCESSING_CUTOFF) return
		if (!lastMessageTimestamp) return
		const base = new Date(lastMessageTimestamp).getTime()
		const delay = Math.max(0, base + processingCutoffSeconds * 1000 - Date.now())
		inactivityTimerRef.current = setTimeout(resetChatId, delay)
	}, [processingCutoffSeconds, lastMessageTimestamp])

	useEffect(() => {
		resetInactivityTimer()
		return () => {
			if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
		}
	}, [resetInactivityTimer, processingCutoffSeconds, lastMessageTimestamp])

	const addCacheMessage = ({
		id,
		content,
		sender,
	}: {
		id: string
		content: string
		sender: LlmChatMessageSenderType
	}) => {
		const message = {
			id,
			content,
			created: new Date().toISOString(),
			responseReceivedAt: null,
			sender,
		}
		if (endpoint === 'auth') {
			const existing = apolloClient.readQuery<PollLlmChatQuery, PollLlmChatQueryVariables>({ query: GET_MESSAGES_QUERY, variables })
			apolloClient.writeQuery<PollLlmChatQuery, PollLlmChatQueryVariables>({
				query: GET_MESSAGES_QUERY,
				variables,
				data: {
					__typename: 'PharosQuery',
					llmChat: {
						__typename: 'LlmChatType',
						id: chatId,
						created: existing?.llmChat?.created ?? new Date().toISOString(),
						processingCutoffSeconds,
						messages: [
							...(existing?.llmChat?.messages ?? []),
							{
								__typename: 'LlmChatMessageType',
								...message,
							},
						],
					},
				},
			})

			
		} else {
			const existing = apolloClient.readQuery<PollLlmChatPubQuery, PollLlmChatPubQueryVariables>({ query: GET_MESSAGES_PUB_QUERY, variables })
			apolloClient.writeQuery<PollLlmChatPubQuery, PollLlmChatPubQueryVariables>({
				query: GET_MESSAGES_PUB_QUERY,
				variables,
				data: {
					__typename: 'PharosQuery',
					llmChatPub: {
						__typename: 'LlmChatPubType',
						id: chatId,
						created: existing?.llmChatPub?.created ?? new Date().toISOString(),
						processingCutoffSeconds,
						messages: [
							...(existing?.llmChatPub?.messages ?? []),
							{
								__typename: 'LlmChatMessagePubType',
								...message,
							},
						],
					},
				},
			})
		}
	}

	const send = async ({ content }: { content: string }) => {
		const id = uuid()
		addCacheMessage({ id, content, sender: LlmChatMessageSenderType.User })
		get({ variables, fetchPolicy: 'cache-only' })
		const resp = await sendMessage({
			variables: {
				id,
				chat: chatId,
				content,
				form: formContext.form.id,
				recordId: formContext.record.id,
			},
		})

		if (resp.errors || !resp.data) {
			return
		}

		let messageId: string
		if (('llmChatMessage_Update' in resp.data)) {
			if (!resp.data?.llmChatMessage_Update?.object) {
				return 
			}
			messageId = resp.data.llmChatMessage_Update.object.id
		} else {
			if (!resp.data?.llmChatMessagePub_Update?.object) {
				return
			}
			messageId = resp.data.llmChatMessagePub_Update.object.id
			
		}

		setStartingStream(true)
		await streamResponse(messageId)
	}

	const streamResponse = (messageId: string) =>
		new Promise<void>((resolve) => {
			const id = uuid()
			addCacheMessage({ id, content: '', sender: LlmChatMessageSenderType.Agent })
			const controller = new AbortController()
			let accumulated = ''

			const headers: Record<string, string> = {}
			if (formContext.publicForm?.code) {
				headers['X-Public-Form-Code'] = formContext.publicForm.code
			}

			fetchEventSource(`/api/v1/llm/messages/${messageId}/stream/${id}`, {
				signal: controller.signal,
				headers,
				onmessage: async (event) => {
					setStartingStream(false)
					if (event.data === '[DONE]') {
						controller.abort()
						await get({ variables, fetchPolicy: 'network-only' })
						resolve()
						return
					}
					try {
						const parsed = JSON.parse(event.data) as { text: string }
						accumulated += parsed.text
						if (endpoint === 'auth') {
							apolloClient.writeFragment({
								id: `LlmChatMessageType:${id}`,
								fragment: gql`
									fragment StreamingMessage on LlmChatMessageType {
										content
									}
								`,
								data: { content: accumulated },
							})
						} else {
							apolloClient.writeFragment({
								id: `LlmChatMessagePubType:${id}`,
								fragment: gql`
									fragment StreamingMessagePub on LlmChatMessagePubType {
										content
									}
								`,
								data: { content: accumulated },
							})
						}
					} catch {
						console.error('Malformed chunk', event.data)
					}
				},
				onerror: () => {
					setStartingStream(false)
					resolve()
					throw new Error('stream error')
				},
			})
		})

	return {
		send,
		awaitingResponse: sending || startingStream,
		messages: (!data ? [] : 'llmChat' in data ? data?.llmChat?.messages : data?.llmChatPub?.messages) || [],
	}
}
